import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { fetchUnits } from "../../../store/unitsStore/unitsThunk";

// Ensures the measuring-unit master is loaded and returns a resolver that maps a
// unit id to its current name (so master renames show everywhere). Falls back to
// the raw token for legacy name-based units.
export const useUnitName = () => {
  const dispatch = useAppDispatch();
  const { units, byId } = useAppSelector((state) => state.units);

  useEffect(() => {
    if (!units || units.length === 0) dispatch(fetchUnits());
  }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  return (unitToken: string) => byId[unitToken]?.name || byId[unitToken]?.code || unitToken;
};

// Extract a unit id whether the value is a raw id string or a populated
// MeasuringUnit object ({ _id, name, ... }).
const idOf = (v: any): string => {
  if (!v) return "";
  if (typeof v === "object") return v._id ? String(v._id) : "";
  return String(v);
};

// Unit option tokens for a SKU = base unit id + conversion unit ids (falls back
// to legacy baseUnitName when no baseUnit id is set).
export const unitOptionTokens = (sku: any): string[] => {
  if (!sku) return [];
  const baseId = idOf(sku.baseUnit);
  const base = baseId ? [baseId] : sku.baseUnitName ? [String(sku.baseUnitName)] : [];
  const conv = sku.unitConversions ? Object.keys(sku.unitConversions) : [];
  return Array.from(new Set([...base, ...conv].filter(Boolean)));
};

// Base units contained in 1 of the chosen unit (for live qty/cost previews).
export const factorOf = (sku: any, unitId: string): number => {
  if (!sku || !unitId) return 0;
  if (String(unitId) === idOf(sku.baseUnit) || String(unitId) === String(sku.baseUnitName)) return 1;
  const f = Number(sku.unitConversions?.[unitId]);
  return f > 0 ? f : 0;
};

// Resolve a SKU's base-unit display name (populated name, id -> name via master, or cache).
export const baseUnitNameOf = (sku: any, resolveName: (id: string) => string): string => {
  if (sku?.baseUnit && typeof sku.baseUnit === "object" && sku.baseUnit.name) return sku.baseUnit.name;
  const id = idOf(sku?.baseUnit);
  return id ? resolveName(id) : sku?.baseUnitName || "units";
};
